import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from 'react';
import MobileImageSliderHero from './MobileImageSliderHero/MobileImageSliderHero';
import ThumbnailSlider from './ThumbnailSlider/ThumbnailSlider';
import MeasurementsAndStyliticsOverlay from '../../features/Images/MeasurementsAndStyliticsOverlay';
import useScrollManagement from '../../tools/customHooks/useScrollManagement';
import useImageLoadingState from './hooks/useImageLoadingState';

const MobileImageSliderWithThumbnails = forwardRef(({
  imageData,
  handleImageErrorOrRemoval,
  onClick,
  brand = 'anf',
  tmntData,
  activeProduct,
  isMobile = true,
  hasPDPMFEBelowTheFold = false,
  isQuickview = false,
}, ref) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const mainSliderRef = useRef(null);
  const thumbnailsRef = useRef(null);

  const handleLoadingComplete = useCallback(() => {
    requestAnimationFrame(() => {
      mainSliderRef.current?.scrollTo({ left: 0, behavior: 'auto' });
    });
  }, []);

  const {
    handleImageLoad,
    handleThumbnailLoad,
    handleImageError: hookHandleImageError,
    resetLoadingState,
    getLoadedImagesCount,
  } = useImageLoadingState(imageData?.length || 0, handleImageErrorOrRemoval, handleLoadingComplete);

  const { handleHeroScroll, scrollToMainImage, scrollToActiveThumbnail } = useScrollManagement(
    activeIndex,
    setActiveIndex,
    imageData,
    mainSliderRef,
    thumbnailsRef,
  );

  const handleImageError = useCallback((imageId) => {
    hookHandleImageError(imageId);
  }, [hookHandleImageError]);

  const handleImageClick = (event, imageDetails) => {
    onClick?.(event, imageDetails);
    mainSliderRef.current?.focus();
  };

  const setSliderFocusOnTouchEnd = () => {
    mainSliderRef.current?.focus();
  };

  const setAltText = useCallback((index, type) => `${type} image ${index + 1}`, []);

  const handleThumbnailClick = (index) => {
    setActiveIndex(index);
    scrollToMainImage(index);
    scrollToActiveThumbnail(index);
  };

  useEffect(() => {
    setActiveIndex(0);
    resetLoadingState();
  }, [imageData, resetLoadingState]);

  useEffect(() => {
    if (activeIndex === undefined || activeIndex < 0 || activeIndex >= (imageData?.length || 0)) {
      return;
    }

    if (thumbnailsRef.current) {
      const activeThumbnail = thumbnailsRef.current.querySelector(`#pdp-thumbnail-${activeIndex}`);
      if (activeThumbnail && activeThumbnail.scrollIntoView) {
        activeThumbnail.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }
  }, [activeIndex, imageData?.length]);

  useImperativeHandle(ref, () => ({
    clearMobileImageLoaderCount: () => {
      resetLoadingState();
    },
    getLoadedImagesCount: () => getLoadedImagesCount(),
    getCurrentIndex: () => activeIndex,
    scrollToImage: (index) => {
      handleThumbnailClick(index);
    },
  }));

  return (
    <section
      className="mobile-image-slider-with-thumbnails"
      data-brand={brand}
      aria-label={`Product image gallery with ${imageData?.length || 0} images`}
    >
      <MobileImageSliderHero
        ref={mainSliderRef}
        productImages={imageData}
        activeIndex={activeIndex}
        onImageClick={handleImageClick}
        onImageError={handleImageError}
        onImageLoad={handleImageLoad}
        onTouchEnd={setSliderFocusOnTouchEnd}
        onScroll={handleHeroScroll}
        setAltText={setAltText}
      />
      <MeasurementsAndStyliticsOverlay
        tmntData={tmntData}
        brand={brand}
        activeProduct={activeProduct}
        isMobile={isMobile}
        hasPDPMFEBelowTheFold={hasPDPMFEBelowTheFold}
        isQuickview={isQuickview}
      />
      <ThumbnailSlider
        ref={thumbnailsRef}
        currentIndex={activeIndex}
        onClick={handleThumbnailClick}
        images={imageData}
        onImageError={handleImageError}
        onImageLoad={handleThumbnailLoad}
      />
    </section>
  );
});

MobileImageSliderWithThumbnails.displayName = 'MobileImageSliderWithThumbnails';

export default MobileImageSliderWithThumbnails;
